import store from '@/store'
import {
  ref, computed, watch, onUnmounted,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// eslint-disable-next-line import/prefer-default-export
export const useFillDataTable = () => {
  const DATA_TABLE_STORE_MODULE_NAME = 'data-table'

  const toast = useToast()
  const refDataListTable = ref(null) //* Ref table
  const isBusyTable = ref(false) //* Table is loading [flag]
  const errorRequest = ref(false) //* Errror in request [flag]
  const perPage = ref(10) //* How many items show per page
  const totalItems = ref(0) //* Total items
  const currentPage = ref(1) //* Current page
  const perPageOptions = [5, 10, 25, 50, 100] //* Page options, how many items will show
  const searchQuery = ref('') //* Search string
  const sortBy = ref('id') //* Propiety from object to sort our items
  const isSortDirDesc = ref(true) //* Items will be sorted desc
  const dataAux = ref(null) //* Temporal table data
  const basicParamRequest = ref({ //* Basic param table request
    q: null,
    perPage: null,
    page: null,
    sortBy: null,
    sortDesc: null,
    dataAux: null,
  })
  const dynamicParamRequest = ref(null) //* Dynamic param request, it is null by default, but you can set an object with filters, getter function, service name
  // *NEW: Debounce timer variable*
  let searchDebounceTimeout = null
  const DEBOUNCE_DELAY = 300 // milliseconds (e.g., 300ms or 500ms is common)

  //* Return how many items do we have, and how many of them we're seeing
  const dataMeta = computed(() => {
    const localItemsCount = refDataListTable.value ? refDataListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItems.value,
    }
  })

  //* Refetch data method
  const refetchData = (serviceCall = false) => {
    // If you want to make http request, send 'serviceCall' as true, this will clear the temporal Array 'dataAux', so the action function, make http request
    if (serviceCall) dataAux.value = null
    if (refDataListTable.value) refDataListTable.value.refresh()
  }

  //* Watching filters to refetch data
  // Si hay dynamic params, asume que es HTTP, sino es local (no limpia dataAux)
  watch([currentPage, perPage], () => { refetchData() })

  // *NEW: Watch para searchQuery con debounce*
  watch(searchQuery, (newVal, oldVal) => {
    // Solo si el valor realmente cambió
    if (newVal === oldVal) {
      return
    }

    // logMessage('debug', `SearchQuery cambió a: "${newVal}". Aplicando debounce.`)

    // Limpiar cualquier temporizador de debounce anterior
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
    }

    // Establecer un nuevo temporizador
    searchDebounceTimeout = setTimeout(() => {
      // Si la búsqueda cambia, es una buena práctica regresar a la primera página
      // para que el usuario vea los resultados desde el inicio.
      if (currentPage.value !== 1) {
        // logMessage('debug', 'Debounce terminó. Reiniciando página a 1.')
        currentPage.value = 1 // Este cambio disparará el watch de [currentPage, perPage]
      } else {
        // Si ya estamos en la página 1, entonces simplemente forzamos el refetch.
        // logMessage('debug', 'Debounce terminó. Refetching data.')
        refetchData() // Forzar la recarga
      }
    }, DEBOUNCE_DELAY)
  })

  // *NEW: Limpiar el temporizador de debounce al desmontar el componente*
  onUnmounted(() => {
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
      // logMessage('debug', 'Composable desmontado. Temporizador de debounce de búsqueda limpiado.')
    }
  })

  //* Make object with basic params and dynamic params
  const fullParamRequest = () => {
    //* Set basic param with basic filters [search query, pagination data, temporal data]
    basicParamRequest.value.q = searchQuery.value
    basicParamRequest.value.perPage = perPage.value
    basicParamRequest.value.page = currentPage.value
    basicParamRequest.value.sortBy = sortBy.value
    basicParamRequest.value.sortDesc = isSortDirDesc.value
    basicParamRequest.value.dataAux = dataAux.value

    // Asegúrate de que dynamicParamRequest.value sea un objeto para Object.assign
    const dynamicParams = dynamicParamRequest.value || {}
    return { ...basicParamRequest.value, ...dynamicParams } // Copiar para no modificar original
  }

  //* Fetch data
  const fetchData = (ctx, callback) => {
    if (dynamicParamRequest.value !== null) { //* Only if we have dynamic params, do fetch table data
      isBusyTable.value = true //* Set loader table as true
      store.dispatch(`${DATA_TABLE_STORE_MODULE_NAME}/fetchTableData`, fullParamRequest())
        .then(response => {
          const { data, total, totalDataArray } = response
          if (dataAux.value === null) dataAux.value = totalDataArray //* If the temporal data it's empty, set all items from request

          isBusyTable.value = false //* Set loader table as false
          errorRequest.value = false //* Set error data as false
          totalItems.value = total
          callback(data)
        })
        .catch(error => {
          isBusyTable.value = false //* Set loader table as false
          errorRequest.value = true //* Set error data as true
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data',
              text: error.message || error,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          },
          { timeout: 50000 })
        })
    }
  }

  return {
    fetchData, //* Fetch data
    refetchData, //* Refetch data
    refDataListTable, //* Ref table
    dataAux, //* Temporal table data
    dynamicParamRequest, //* Dynamic param request, it is null by default, but you can set an object with filters, getter function, service name

    // Pagination
    dataMeta,

    // Filters selected
    perPage,
    totalItems,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,

    // Loader
    isBusyTable,

    // Error
    errorRequest,
  }
}
